import type { UseControllerProps } from "react-hook-form";

import ResizeTextarea from "react-textarea-autosize";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { TextActionOptional } from "src/features/observations/components/TextActionOptional";
import { ChangeEvent, useEffect, useState } from "react";
import { useDebounce } from "react-use";

interface TextQuestionProps extends UseControllerProps<ObservationFormState> {
	questionNumber: number;

	staticInput?: boolean;

	showAction?: boolean;
}

const TextQuestion = ({
	name,
	rules,
	questionNumber,
	staticInput,
	showAction
}: TextQuestionProps) => {
	const { control } = useFormContext<ObservationFormState>();

	const {
		field: { onChange, value }
	} = useController({
		control,
		name,
		rules
	});

	const { isDisabled } = useWatch({
		control
	});

	const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		if (staticInput) return;

		setSavedValue(event.target.value);
	};

	const [savedValue, setSavedValue] = useState<string>(
		(value as string | null | undefined) ?? ""
	);

	useEffect(() => {
		// We want to include "" values; null/undefined fall back to ""
		// otherwise React would warn about a controlled→uncontrolled flip.
		if (value !== null && value !== undefined) {
			setSavedValue(value as string);
		}
	}, [value]);

	useDebounce(
		() => {
			onChange(savedValue);
		},
		1000,
		[savedValue]
	);

	return (
		<div>
			<ResizeTextarea
				id={`question-${questionNumber}`}
				aria-labelledby={`question-${questionNumber}-label`}
				placeholder="Please enter your answer"
				className="w-full mt-4 px-3 py-2 bg-white border border-border rounded-md text-base resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
				disabled={isDisabled}
				value={savedValue}
				onChange={handleChange}
			/>

			{showAction && (
				<TextActionOptional
					name={`questions.${questionNumber - 1}.action`}
					questionNumber={questionNumber}
					staticInput={staticInput}
				/>
			)}
		</div>
	);
};

export { TextQuestion, type TextQuestionProps };
